import { forwardRef } from "react";
import { Instagram, Globe, MapPin } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/contexts/I18nContext";
import { useGeoLocation } from "@/contexts/LocationContext";
import { LANGUAGE_LABELS, type Language } from "@/lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const LANGUAGE_FLAGS: Record<Language, string> = {
  en: "🇬🇧",
  nl: "🇳🇱",
  de: "🇩🇪",
  fr: "🇫🇷",
  es: "🇪🇸",
  it: "🇮🇹",
  pt: "🇧🇷",
  sv: "🇸🇪",
  pl: "🇵🇱",
  ja: "🇯🇵",
  ko: "🇰🇷",
  ru: "🇷🇺",
  tr: "🇹🇷",
  da: "🇩🇰",
  no: "🇳🇴",
  fi: "🇫🇮",
  cs: "🇨🇿",
};

const FOOTER_STYLES = [
  { name: "Traditional", slug: "traditional" },
  { name: "Realism", slug: "realism" },
  { name: "Japanese", slug: "japanese" },
  { name: "Fine Line", slug: "fine-line" },
  { name: "Watercolor", slug: "watercolor" },
  { name: "Neo-Traditional", slug: "neo-traditional" },
  { name: "Blackwork", slug: "blackwork" },
  { name: "Geometric", slug: "geometric" },
  { name: "Dotwork", slug: "dotwork" },
  { name: "Black & Grey", slug: "black-grey" },
  { name: "Illustrative", slug: "illustrative" },
  { name: "Micro Realism", slug: "micro-realism" },
];

const GlobalFooter = forwardRef<HTMLElement>(function GlobalFooter(_props, ref) {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useI18n();
  const userLocation = useGeoLocation();

  // Fetch top cities for footer
  const { data: footerCities } = useQuery({
    queryKey: ["footer-cities"],
    queryFn: async () => {
      const { data } = await supabase
        .from("city_stats")
        .select("city, city_slug, country")
        .order("studio_count", { ascending: false })
        .limit(10);
      return data || [];
    },
    staleTime: 300_000, // 5 min cache
  });

  const defaultCitySlug = userLocation.detectedCitySlug || "amsterdam";
  const countrySlug = userLocation.countrySlug || "nl";

  return (
    <footer ref={ref} className="border-t border-border/50 bg-background py-16 mb-16 md:mb-0">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10 mb-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1 space-y-4">
            <Link to="/">
              <img src="/logo_groot.svg" alt="Inkvaluator" className="h-8 w-auto mb-1" />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              {t("footer.tagline_expanded") ||
                "Vind de beste tattoo artiesten, vergelijk stijlen en prijzen, en boek met vertrouwen."}
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/inkvaluator"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:text-primary hover:border-primary transition-all"
              >
                <Instagram className="w-3.5 h-3.5" /> @Inkvaluator
              </a>
            </div>

            {/* Language picker */}
            <div className="pt-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-fit gap-2 text-xs border-border bg-background/50 text-foreground"
                  >
                    <span>{LANGUAGE_FLAGS[language]}</span>
                    <Globe className="w-3.5 h-3.5" />
                    {LANGUAGE_LABELS[language]}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
                  {(Object.keys(LANGUAGE_LABELS) as Language[]).map((lang) => (
                    <DropdownMenuItem
                      key={lang}
                      onClick={() => setLanguage(lang)}
                      className={language === lang ? "bg-primary/10 text-primary font-medium" : ""}
                    >
                      <span className="mr-2">{LANGUAGE_FLAGS[lang]}</span>
                      {LANGUAGE_LABELS[lang]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Explore */}
          <div className="space-y-4">
            <p className="text-sm font-semibold text-foreground">{t("footer.explore") || "Verkennen"}</p>
            <div className="flex flex-col gap-2.5">
              {[
                { path: "/tattoo", label: t("footer.find_artist") || "Vind een Artiest" },
                { path: "/demo-calculator", label: t("footer.price_calculator") || "Prijscalculator" },
                { path: "/artiesten", label: t("footer.discover_artists") },
                { path: "/trends", label: t("footer.style_guide") || "Stijlgids" },
                { path: "/blog", label: "Blog" },
              ].map(({ path, label }) => (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors text-left"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Popular Cities */}
          <div className="space-y-4">
            <p className="text-sm font-semibold text-foreground">{t("footer.popular_cities") || "Populaire Steden"}</p>
            <div className="flex flex-col gap-2.5">
              {(footerCities || []).slice(0, 8).map((city: any) => (
                <Link
                  key={city.city_slug}
                  to={`/tattoo/${(city.country || "NL").toLowerCase()}/${city.city_slug}`}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {city.city}
                </Link>
              ))}
              <Link to="/tattoo" className="text-sm text-primary hover:underline">
                {t("footer.all_cities") || "Alle steden →"}
              </Link>
            </div>
          </div>

          {/* Tattoo Styles */}
          <div className="space-y-4">
            <p className="text-sm font-semibold text-foreground">{t("footer.tattoo_styles") || "Tattoo Stijlen"}</p>
            <div className="flex flex-col gap-2.5">
              {FOOTER_STYLES.slice(0, 8).map(({ name, slug }) => (
                <Link
                  key={slug}
                  to={`/tattoo/${countrySlug}/${defaultCitySlug}/stijl/${slug}`}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {name}
                </Link>
              ))}
              <Link to={`/tattoo/${countrySlug}/${defaultCitySlug}`} className="text-sm text-primary hover:underline">
                {t("footer.all_styles") || "Alle stijlen →"}
              </Link>
            </div>
          </div>

          {/* Resources */}
          <div className="space-y-4">
            <p className="text-sm font-semibold text-foreground">{t("footer.resources") || "Resources"}</p>
            <div className="flex flex-col gap-2.5">
              {[
                { path: "/hoe-werkt-het", label: t("footer.how_it_works") },
                { path: "/claim", label: t("footer.claim_profile") || "Claim je Profiel" },
                { path: "/support", label: t("footer.support") },
                { path: "/privacy", label: t("footer.privacy") },
                { path: "/data-deletion", label: t("footer.data_deletion") || "Data Deletion" },
                { path: "/terms-of-service", label: t("footer.terms") },
              ].map(({ path, label }) => (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors text-left"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="h-px bg-border mb-6" />
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <p>© 2026 Inkvaluator. {t("footer.all_rights")}</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-primary transition-colors">
              {t("footer.privacy")}
            </Link>
            <Link to="/terms-of-service" className="hover:text-primary transition-colors">
              {t("footer.terms")}
            </Link>
            <Link to="/support" className="hover:text-primary transition-colors">
              {t("footer.support")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
});

GlobalFooter.displayName = "GlobalFooter";

export default GlobalFooter;
